import { useEffect, useState } from "react";
import { Download, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useT } from "@/i18n/LanguageContext";

const DISMISS_KEY = "keepflow.installDismissed";

function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)")?.matches ||
    window.navigator.standalone === true
  );
}
function isIOS() {
  if (typeof navigator === "undefined") return false;
  return /iPhone|iPad|iPod/.test(navigator.userAgent) && !window.MSStream;
}

export default function InstallButton() {
  const { t } = useT();
  const [deferred, setDeferred] = useState(null);
  const [installed, setInstalled] = useState(isStandalone());
  const [iosOpen, setIosOpen] = useState(false);

  useEffect(() => {
    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    const onInstalled = () => {
      setInstalled(true);
      setDeferred(null);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  if (installed) return null;

  // Nothing to show on desktops that aren't a Chromium/Edge/Chrome that fired the event,
  // and the user isn't on iOS — keep the button hidden rather than mislead them.
  if (!deferred && !isIOS()) return null;

  const click = async () => {
    if (isIOS() && !deferred) {
      setIosOpen(true);
      return;
    }
    if (!deferred) return;
    deferred.prompt();
    const choice = await deferred.userChoice.catch(() => null);
    setDeferred(null);
    if (choice?.outcome !== "accepted") {
      localStorage.setItem(DISMISS_KEY, Date.now().toString());
    }
  };

  return (
    <>
      <button
        onClick={click}
        data-testid="install-btn"
        className="hidden sm:inline-flex items-center gap-1.5 h-10 px-3 rounded-full border border-amber-300 dark:border-amber-700/50 bg-amber-50 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200 font-medium text-sm hover:shadow-md transition-all"
      >
        <Download className="h-4 w-4" />
        <span className="hidden lg:inline">{t("install.cta")}</span>
      </button>
      <button
        onClick={click}
        data-testid="install-btn-mobile"
        className="sm:hidden h-10 w-10 inline-flex items-center justify-center rounded-full border border-amber-300 dark:border-amber-700/50 bg-amber-50 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200"
        aria-label={t("install.cta")}
      >
        <Download className="h-4 w-4" />
      </button>

      <Dialog open={iosOpen} onOpenChange={setIosOpen}>
        <DialogContent className="max-w-sm" data-testid="install-ios-dialog">
          <DialogHeader>
            <DialogTitle>{t("install.iosTitle")}</DialogTitle>
            <DialogDescription>{t("install.iosHelp")}</DialogDescription>
          </DialogHeader>
          <Button onClick={() => setIosOpen(false)} className="rounded-full">
            {t("install.close")}
          </Button>
        </DialogContent>
      </Dialog>
    </>
  );
}
